import { CSSProperties, FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretLeft, CheckCircle, IdentificationCard, Info, Spinner, Wallet } from "@phosphor-icons/react";
import { useStartSetupTask } from "../components/CompleteSetupTaskList";
import useLoanPreApprovalRequirements from "../hooks/useLoanPreApprovalRequirements";
import { APIError } from "../api";
import { useAuth } from "../context/AuthContext";
import useAlertEnquiry from "../hooks/useAlertEnquiry";
import useCreateAlertAccount from "../hooks/useCreateAlertAccount";
import { LoanPreApprovalQuote, useLoanPreApprovalQuote, useSubmitLoanPreApproval } from "../hooks/useLoanPreApproval";
import styles from "./LoanPreApproval.module.scss";

const MIN_REQUEST_AMOUNT = 1000;
const MAX_REQUEST_AMOUNT = 999999999999;
// This is the slider's range, not a new lending limit. Manual entry stays available.
const AMOUNT_SLIDER_MAX = 1500000;
const AMOUNT_STEP = 100;
const formatAmount = (value: number) => `₦${value.toLocaleString("en-NG")}`;

const PURPOSES = [{
  value: "flight",
  label: "Flight"
}, {
  value: "vendor_purchase",
  label: "Shopping"
}, {
  value: "education",
  label: "Education"
}, {
  value: "health",
  label: "Health"
}, {
  value: "personal",
  label: "Personal"
}, {
  value: "other",
  label: "Other"
}];
const formatMoney = (value: number) => new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  minimumFractionDigits: 2
}).format(value);

// Provider names stay in the API contract, not in customer-facing messages.
const customerMessage = (error: unknown, fallback: string) => {
  const message = error instanceof Error ? error.message : fallback;
  return message.replace(/Unable to verify your Alert account balance/gi, "We couldn't check your loan account balance").replace(/Alert account/gi, "loan account").replace(/\bAlert\b/gi, "lending partner").replace(/\bcommission\b/gi, "processing fee");
};
const primaryButton = "rounded-xl bg-deepBlue px-6 py-3 font-medium text-white hover:bg-deepBlue/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-deepBlue disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2";
const secondaryButton = "rounded-xl border border-gray-300 px-6 py-3 font-medium text-deepBlue hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed";

type SavedRequest = { request_id: string; amount: number; purpose: string };
const requestStorageKey = (userId: string) => `visaro_preapproval_request:${userId}`;
const readSavedRequest = (userId: string): SavedRequest | null => {
  if (!userId) return null;
  try {
    const saved = JSON.parse(sessionStorage.getItem(requestStorageKey(userId)) || "null");
    return saved && typeof saved.request_id === "string" && /^[0-9a-f-]{36}$/i.test(saved.request_id)
      && Number.isFinite(saved.amount) && saved.amount >= 1000
      && PURPOSES.some((item) => item.value === saved.purpose) ? saved : null;
  } catch { return null; }
};

const LoanPreApproval = () => {
  const navigate = useNavigate();
  const {
    user,
    refetch
  } = useAuth();
  const { ready: kycReady, missing } = useLoanPreApprovalRequirements();
  const startSetupTask = useStartSetupTask();
  const userId = String(user?.id ?? "");
  const {
    alertEnquiryData,
    fetchingAlertAccount,
    refetchAlertAccount
  } = useAlertEnquiry({
    enabled: !!user?.id
  });
  const {
    createAlertAccountAsync
  } = useCreateAlertAccount();
  const quoteMutation = useLoanPreApprovalQuote();
  const requestMutation = useSubmitLoanPreApproval();
  const [step, setStep] = useState<"form" | "review" | "success">("form");
  const [savedRequest, setSavedRequest] = useState<SavedRequest | null>(() => readSavedRequest(userId));
  const [amount, setAmount] = useState(savedRequest ? String(savedRequest.amount) : String(MIN_REQUEST_AMOUNT));
  const [purpose, setPurpose] = useState(savedRequest?.purpose ?? "");
  const [quote, setQuote] = useState<LoanPreApprovalQuote | null>(null);
  const [consented, setConsented] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [supportReference, setSupportReference] = useState("");
  const [activating, setActivating] = useState(false);
  const submitting = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const accountNumber = alertEnquiryData?.Message?.AccountNumber;
  const hasLoanAccount = Boolean(user?.account_alert === 1 || accountNumber);
  const busy = quoteMutation.isLoading || requestMutation.isLoading || activating;
  const sliderAmount = Math.min(AMOUNT_SLIDER_MAX, Math.max(MIN_REQUEST_AMOUNT, Number(amount) || MIN_REQUEST_AMOUNT));
  const sliderStyle = { "--amount-progress": `${(sliderAmount - MIN_REQUEST_AMOUNT) / (AMOUNT_SLIDER_MAX - MIN_REQUEST_AMOUNT) * 100}%` } as CSSProperties;
  useEffect(() => {
    headingRef.current?.focus();
    window.scrollTo({
      top: 0,
      left: 0
    });
  }, [step]);
  const activateAccount = async () => {
    if (activating) return;
    setActivating(true);
    setErrorMessage("");
    let creationError: unknown;
    try {
      try {
        await createAlertAccountAsync();
      } catch (error) {
        creationError = error;
      }
      const [profileResult, enquiryResult] = await Promise.all([refetch().catch(() => null), refetchAlertAccount().catch(() => null)]);
      if (!(profileResult?.data?.data?.account_alert === 1 || enquiryResult?.data?.Message?.AccountNumber)) {
        throw creationError instanceof Error ? creationError : new Error("We couldn't set up your loan account. Please try again.");
      }
    } catch (error) {
      setErrorMessage(customerMessage(error, "We couldn't set up your loan account. Please try again."));
    } finally {
      setActivating(false);
    }
  };
  const getQuote = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !kycReady || !hasLoanAccount) return;
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < MIN_REQUEST_AMOUNT) {
      setErrorMessage("Enter an amount of at least ₦1,000.");
      return;
    }
    if (!purpose) {
      setErrorMessage("Select what you need the loan for.");
      return;
    }
    setErrorMessage("");
    try {
      const response = await quoteMutation.mutateAsync(numericAmount);
      if (!response.data) throw new Error("We couldn't load your quote. Please try again.");
      setQuote(response.data);
      setConsented(false);
      setStep("review");
    } catch (error) {
      setErrorMessage(customerMessage(error, "We couldn't load your quote. Please try again."));
    }
  };
  const submitRequest = async () => {
    if (!quote || !consented || busy || submitting.current || !kycReady || !hasLoanAccount) return;
    submitting.current = true;
    setErrorMessage("");
    try {
      const submission = savedRequest ?? {
        request_id: crypto.randomUUID(),
        amount: quote.requested_amount,
        purpose
      };
      // Save BEFORE sending. A refresh or lost response must reuse the same fee reference.
      try {
        sessionStorage.setItem(requestStorageKey(userId), JSON.stringify(submission));
      } catch {
        throw new Error("We couldn't save your request reference. No fee was charged. Please try again or contact support.");
      }
      setSavedRequest(submission);
      setSupportReference(submission.request_id);
      await requestMutation.mutateAsync(submission);
      setStep("success");
      try { sessionStorage.removeItem(requestStorageKey(userId)); } catch { /* retain a safe replay key */ }
      setSavedRequest(null);
    } catch (error) {
      if (error instanceof APIError) {
        const code = error.responseData?.error_code;
        if (typeof error.responseData?.request_id === "string") setSupportReference(error.responseData.request_id);
        // Only explicit pre-charge failures may discard a reference and allow edits.
        if (["PREAPPROVAL_BALANCE_UNAVAILABLE", "PREAPPROVAL_INSUFFICIENT_BALANCE", "PREAPPROVAL_ACCOUNT_MISSING", "PREAPPROVAL_WALLET_MISSING", "PREAPPROVAL_PARTNER_UNAVAILABLE", "PREAPPROVAL_CONFIGURATION_INVALID", "PREAPPROVAL_LOAN_ALREADY_PENDING"].includes(code)) {
          try { sessionStorage.removeItem(requestStorageKey(userId)); } catch { /* safe old reference */ }
          setSavedRequest(null);
        }
      }
      setErrorMessage(customerMessage(error, "We couldn't submit your request. Check your loan requests before trying again, or contact support."));
    } finally {
      submitting.current = false;
    }
  };
  const back = () => {
    if (busy) return;
    setErrorMessage("");
    if (step === "review" && !savedRequest) {
      setConsented(false);
      setStep("form");
    } else navigate("/home");
  };
  const errorNotice = errorMessage && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-700">
      {errorMessage}
    </div>;
  return <div className="px-5 py-10 sm:py-16 text-deepBlue">
      <div className="mx-auto max-w-4xl">
        <button type="button" onClick={back} disabled={busy} className="mb-8 flex items-center gap-2 text-sm text-gray-500 hover:text-deepBlue disabled:opacity-50">
          <CaretLeft size={18} /> {step === "review" && !savedRequest ? "Edit request" : "Back to dashboard"}
        </button>
        <header className="mb-8">
          <p className="mb-2 text-sm font-medium text-visaro-orange">Loan pre-approval</p>
          <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-semibold outline-none">
            {step === "success" ? "Your request has been submitted" : step === "review" ? "Review your loan request" : "What do you need a loan for?"}
          </h1>
          <p className="mt-3 max-w-2xl leading-6 text-gray-500">
            {step === "success" ? "Your request is awaiting review. You can follow its status in your loans." : step === "review" ? "Check the amount and processing fee before you submit. Submitting a request does not guarantee approval." : "Choose an amount and purpose. We'll show what you need to have available and what you'll be charged before you submit."}
          </p>
        </header>

        {step === "success" ? <section className="rounded-xl border border-gray-200 p-6 sm:p-8">
            <CheckCircle size={48} weight="fill" className="mb-5 text-green-600" />
            <h2 className="text-xl font-semibold">What happens next?</h2>
            <p className="mt-3 leading-6 text-gray-500">We'll update your loan status once a decision is available. This request hasn't booked a flight or placed an order.</p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => navigate("/payments/loans")} className={primaryButton}>View my loans</button>
              <button type="button" onClick={() => navigate("/home")} className={secondaryButton}>Back to dashboard</button>
            </div>
          </section> : fetchingAlertAccount ? <div role="status" className="flex items-center gap-3 py-12 text-gray-500"><Spinner size={22} className="animate-spin" /> Checking your account…</div> : !kycReady ? <section className="rounded-xl border border-gray-200 p-6 sm:p-8">
            <IdentificationCard size={36} className="mb-4 text-visaro-orange" />
            <h2 className="text-xl font-semibold">Complete your profile first</h2>
            <p className="mt-3 leading-6 text-gray-500">Complete these remaining steps before requesting a loan. Living outside Nigeria does not prevent you from requesting pre-approval.</p>
            <div className="mt-6 flex flex-col gap-3">
              {missing.map((task) => <button key={task.route} type="button" onClick={() => startSetupTask(task, { returnTo: "/loans/request" })} className={secondaryButton}>{task.label}</button>)}
            </div>
          </section> : !hasLoanAccount ? <section className="rounded-xl border border-gray-200 p-6 sm:p-8 space-y-5">
            <Wallet size={36} className="text-visaro-orange" />
            <h2 className="text-xl font-semibold">Set up your loan account</h2>
            <p className="leading-6 text-gray-500">You'll need a linked loan account for the balance check and processing fee. This is separate from your Visaro wallet.</p>
            {errorNotice}
            <button type="button" onClick={activateAccount} disabled={activating} className={primaryButton}>
              {activating && <Spinner size={18} className="animate-spin" />}{activating ? "Setting up…" : "Set up and continue"}
            </button>
          </section> : step === "form" ? <form onSubmit={getQuote} className="grid gap-8 md:grid-cols-2">
            <section className="space-y-6">
              {savedRequest && <p role="status" className="rounded-xl bg-blue-50 p-4 text-sm leading-6">You have a saved request. Review it to resume with the same payment reference. If its payment is uncertain, contact support before starting another request.</p>}
              <div>
                <label htmlFor="loan-request-amount" className="font-medium">How much do you need?</label>
                <div className="mt-3 flex items-center rounded-xl border border-gray-300 focus-within:ring-2 focus-within:ring-deepBlue/50">
                  <span className="pl-4 text-xl text-gray-500" aria-hidden="true">₦</span>
                  <input id="loan-request-amount" type="number" inputMode="decimal" min={MIN_REQUEST_AMOUNT} max={MAX_REQUEST_AMOUNT} step={AMOUNT_STEP} value={amount} onChange={event => {
                setAmount(event.target.value);
                setErrorMessage("");
              }} aria-describedby="loan-amount-help" required disabled={busy || Boolean(savedRequest)} className="min-w-0 w-full rounded-xl px-3 py-4 text-2xl font-semibold tabular-nums outline-none" />
                </div>
                <label htmlFor="loan-amount-slider" className="sr-only">Adjust loan amount</label>
                <input id="loan-amount-slider" type="range" min={MIN_REQUEST_AMOUNT} max={AMOUNT_SLIDER_MAX} step={AMOUNT_STEP} value={sliderAmount} onChange={event => {
                  setAmount(event.target.value);
                  setErrorMessage("");
                }} aria-valuetext={formatAmount(sliderAmount)} aria-describedby="loan-amount-help" disabled={busy || Boolean(savedRequest)} className={styles.amountSlider} style={sliderStyle} />
                <div className="flex justify-between gap-3 text-sm tabular-nums text-gray-500" aria-hidden="true">
                  <span>{formatAmount(MIN_REQUEST_AMOUNT)}</span>
                  <span>{formatAmount(AMOUNT_SLIDER_MAX)}</span>
                </div>
                <p id="loan-amount-help" className="mt-3 text-sm leading-6 text-gray-500">Slide to choose an amount, or type one above. Minimum request: {formatAmount(MIN_REQUEST_AMOUNT)}. You can type an amount above the slider range.</p>
              </div>
              <label className="block">
                <span className="font-medium">What will you use it for?</span>
                <select required disabled={busy || Boolean(savedRequest)} value={purpose} onChange={event => {
              setPurpose(event.target.value);
              setErrorMessage("");
            }} className="mt-3 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-deepBlue/50">
                  <option value="">Select a purpose</option>
                  {PURPOSES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>
              {errorNotice}
              <button type="submit" disabled={busy} className={`${primaryButton} w-full`}>
                {quoteMutation.isLoading && <Spinner size={18} className="animate-spin" />}{quoteMutation.isLoading ? "Preparing your quote…" : "Review request"}
              </button>
            </section>
            <aside className="h-fit rounded-xl border border-blue-100 bg-blue-50/50 p-6">
              <Info size={24} className="mb-4 text-blue-500" />
              <h2 className="text-lg font-semibold">Before you submit</h2>
              <ul className="mt-4 space-y-4 text-sm leading-6 text-gray-600">
                <li>You'll need 30% of your requested amount, plus the processing fee, available in your linked loan account.</li>
                <li>The 30% is a balance check only. It is not deducted when you submit.</li>
                <li>You'll review and agree to the processing fee before it is charged. Viewing a quote doesn't charge you.</li>
              </ul>
            </aside>
          </form> : quote && <div className="grid gap-8 md:grid-cols-2">
            <section>
              <p className="text-gray-500">You're requesting</p>
              <p className="mt-2 text-3xl font-semibold break-words">{formatMoney(quote.requested_amount)}</p>
              <p className="mt-2 text-gray-500">For {PURPOSES.find(item => item.value === purpose)?.label.toLowerCase()}</p>
              <div className="mt-7 rounded-xl border border-blue-100 bg-blue-50/50 p-5 text-sm leading-6 text-gray-600">
                <p className="font-medium text-deepBlue">Your linked loan account{accountNumber ? ` · ${accountNumber}` : ""}</p>
                <p className="mt-2">The balance check and processing fee use this account, not your Visaro wallet balance.</p>
              </div>
              <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm leading-6 text-gray-700">
                <input type="checkbox" checked={consented} disabled={busy} onChange={event => {
              setConsented(event.target.checked);
              setErrorMessage("");
            }} className="mt-1 h-4 w-4 shrink-0 accent-deepBlue" />
                <span>I agree to the {formatMoney(quote.commission_fee)} processing fee being deducted from my linked loan account when I submit. The {formatMoney(quote.balance_requirement)} balance requirement is not deducted at this stage.</span>
              </label>
              <div className="mt-5">{errorNotice}</div>
              {(savedRequest || supportReference) && <div className="mt-4 space-y-2 text-sm leading-6 text-gray-500">
                <p>{savedRequest ? "Retries use the same reference. A confirmed processing fee is not charged again." : "No processing fee was charged for this attempt. You can edit and review your request."}</p>
                <p className="break-all">Request reference: {supportReference || savedRequest?.request_id}</p>
                <button type="button" onClick={() => navigate("/support")} className="font-medium text-visaro-orange underline">Contact support</button>
                <button type="button" onClick={() => navigate("/payments/loans")} className="ml-4 font-medium text-visaro-orange underline">View my loans</button>
              </div>}
              <button type="button" onClick={submitRequest} disabled={!consented || busy} className={`${primaryButton} mt-6 w-full`}>
                {requestMutation.isLoading && <Spinner size={18} className="animate-spin" />}{requestMutation.isLoading ? "Submitting your request…" : savedRequest ? "Resume this request" : "Agree and submit request"}
              </button>
            </section>
            <aside className="order-first h-fit overflow-hidden rounded-xl border border-gray-200 md:order-last">
              <h2 className="px-5 py-5 text-lg font-semibold">What you need available</h2>
              <dl className="text-sm">
                <div className="flex justify-between gap-4 border-t border-gray-100 px-5 py-4"><dt className="text-gray-500">Balance requirement (30%)</dt><dd className="font-semibold text-right">{formatMoney(quote.balance_requirement)}</dd></div>
                <div className="flex justify-between gap-4 border-t border-gray-100 px-5 py-4"><dt className="text-gray-500">Processing fee</dt><dd className="font-semibold text-right">{formatMoney(quote.commission_fee)}</dd></div>
                <div className="flex justify-between gap-4 bg-deepBlue px-5 py-5 text-white"><dt>Minimum account balance</dt><dd className="font-semibold text-right">{formatMoney(quote.minimum_alert_balance)}</dd></div>
              </dl>
              <div className="px-5 py-5 text-sm leading-6 text-gray-500">Only the <strong className="text-deepBlue">{formatMoney(quote.commission_fee)} processing fee</strong> is charged on submission. The remaining balance is checked, not charged.</div>
            </aside>
          </div>}
      </div>
    </div>;
};
export default LoanPreApproval;
